import { useEffect, useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import { useT } from "@/i18n";
import { closeTabletRequest, fetchCameraRequest, type CameraRequest } from "@/services/cameraRequests";

// His "Photo with the tablet" on the PC (camTablet): the request sent, then "✓ n photos received from
// the tablet" as they arrive (or "✓ Delivery note (Receiving)" once the tablet took a Receiving request);
// "Done" closes the request. `onPhotos` lets the page reload the files that arrived — with the request as
// it is now, which names the run the tablet's first photo made for a job not started.
export default function TabletWait({ request, onPhotos, onClose }: { request: CameraRequest; onPhotos?: (now: CameraRequest) => void; onClose: () => void }) {
  const { t } = useT();
  const q = useQuery({ queryKey: ["camera-request", request.id], queryFn: () => fetchCameraRequest(request.id), refetchInterval: 2500, initialData: request });
  const r = q.data;
  const seen = useRef(request.photo_count);
  useEffect(() => {
    if (r.photo_count !== seen.current) { seen.current = r.photo_count; onPhotos?.(r); }
  }, [r, onPhotos]);
  // His `end`: the request is closed once, by Done, by Escape, or when this panel goes with the dialog
  // it was opened from — it never stays open on the station.
  const closed = useRef(false);
  const close = () => { if (!closed.current) { closed.current = true; void closeTabletRequest(request.id).catch(() => {}); } };
  const done = () => { close(); onClose(); };
  // His wait sheet lives in its own layer (camv): Escape closes it only — caught first (capture phase)
  // and stopped there, so the Finish sheet underneath keeps its tick and its note.
  const doneRef = useRef(done);
  doneRef.current = done;
  useEffect(() => {
    const key = (e: KeyboardEvent) => { if (e.key === "Escape") { e.stopImmediatePropagation(); doneRef.current(); } };
    window.addEventListener("keydown", key, true);
    return () => window.removeEventListener("keydown", key, true);
  }, []);
  // Closed on the way out, a tick later: React's development double mount re-runs this at once and
  // cancels it, so only a real unmount closes the request.
  const pending = useRef<number | undefined>(undefined);
  const closeRef = useRef(close);
  closeRef.current = close;
  useEffect(() => {
    window.clearTimeout(pending.current);
    return () => { pending.current = window.setTimeout(() => closeRef.current(), 0); };
  }, []);
  const state = r.mode === "rec" ? (r.status === "taken" ? `✓ ${t("st.recL")}` : t("cam.got0"))
    : r.photo_count > 0 ? t(r.photo_count > 1 ? "cam.gotMany" : "cam.gotOne", { n: r.photo_count }) : t("cam.got0");
  return (
    <div role="dialog" aria-modal="true" className="fixed inset-0 z-50 flex items-start justify-center bg-scrim/50 p-4">
      <div className="mt-16 w-full max-w-md space-y-3 rounded-xl bg-white p-5 shadow-xl">
        <h2 className="text-lg font-semibold text-slate-900">{t("cam.waitT")}</h2>
        <p className="text-sm text-slate-700">{r.mode === "rec" ? t("cam.waitRec") : t("cam.wait", { l: r.label ?? "" })}</p>
        <p className="rounded-lg bg-slate-50 px-3 py-2 text-sm font-medium text-slate-800 ring-1 ring-slate-200">{state}</p>
        <div className="flex justify-end"><button onClick={done} className="rounded-lg bg-slate-900 px-4 py-2 text-sm font-semibold text-white">{t("cam.fin")}</button></div>
      </div>
    </div>
  );
}
